
import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import BackofficeLayout from "@/components/layouts/BackofficeLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Building, MapPin, Mail, Phone, FileText, Home, Pencil } from "lucide-react";
import { associationApi } from "@/services/api";
import { Loader2 } from "lucide-react";

const mockAssociations = {
  "assoc-001": {
    id: "assoc-001",
    name: "Sunset Condominiums",
    type: "Condominium",
    email: "manager@sunsetcondos.com",
    phone: "(305) 555-1234",
    address: "123 Ocean Blvd, Miami, FL 33139",
    taxId: "12-3456789",
    taxClassification: "LLC - Partnership",
    documents: [
      { id: "doc-001", name: "Resale Certificate", type: "Financial", price: 75 },
      { id: "doc-002", name: "HOA Financial Statement", type: "Financial", price: 25 },
      { id: "doc-003", name: "Bylaws & CC&Rs", type: "Legal", price: 45 },
      { id: "doc-004", name: "Meeting Minutes", type: "Governance", price: 15 },
    ],
    properties: [
      { id: "prop-001", address: "123 Ocean Blvd, Unit 101", city: "Miami", state: "FL", zip: "33139" },
      { id: "prop-002", address: "123 Ocean Blvd, Unit 102", city: "Miami", state: "FL", zip: "33139" },
      { id: "prop-003", address: "123 Ocean Blvd, Unit 103", city: "Miami", state: "FL", zip: "33139" },
      { id: "prop-004", address: "123 Ocean Blvd, Unit 104", city: "Miami", state: "FL", zip: "33139" },
      { id: "prop-005", address: "123 Ocean Blvd, Unit 105", city: "Miami", state: "FL", zip: "33139" },
    ]
  },
  "assoc-002": {
    id: "assoc-002",
    name: "Highland Heights HOA",
    type: "HOA",
    email: "info@highlandheightshoa.org",
    phone: "(404) 555-6789",
    address: "456 Highland Ave, Atlanta, GA 30307",
    taxId: "98-7654321",
    taxClassification: "LLC - C Corporation",
    documents: [
      { id: "doc-005", name: "Resale Package", type: "Financial", price: 125 },
      { id: "doc-006", name: "HOA Bylaws", type: "Legal", price: 35 },
      { id: "doc-007", name: "Annual Budget", type: "Financial", price: 20 },
    ],
    properties: [
      { id: "prop-006", address: "456 Highland Ave, Lot 1", city: "Atlanta", state: "GA", zip: "30307" },
      { id: "prop-007", address: "456 Highland Ave, Lot 2", city: "Atlanta", state: "GA", zip: "30307" },
      { id: "prop-008", address: "456 Highland Ave, Lot 3", city: "Atlanta", state: "GA", zip: "30307" },
    ]
  }
};

const AssociationDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [association, setAssociation] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const { data, isLoading, error } = useQuery({
    queryKey: ["association", id],
    queryFn: () => associationApi.getById(Number(id)),
    enabled: !!id,
    meta: {
      onError: (err: Error) => {
        console.error("Error fetching association:", err);
        setLoading(false);
      }
    }
  });

  useEffect(() => {
    if (data) {
      setAssociation(data);
      setLoading(false);
    } else if (!isLoading) {
      setLoading(false);
    }
  }, [data, isLoading]);

  if (loading || isLoading) {
    return (
      <BackofficeLayout>
        <div className="flex justify-center items-center h-[60vh]">
          <Loader2 className="h-12 w-12 animate-spin text-brand-blue" />
        </div>
      </BackofficeLayout>
    );
  }

  if (!association) {
    return (
      <BackofficeLayout>
        <div className="space-y-6">
          <div className="flex items-center">
            <Button variant="outline" size="icon" onClick={() => navigate("/backoffice/associations")} className="mr-4">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-3xl font-bold tracking-tight">Association Not Found</h1>
          </div>
          <p className="text-gray-500">
            The association you're looking for could not be found.
          </p>
          <Button asChild>
            <Link to="/backoffice/associations">Back to Associations</Link>
          </Button>
        </div>
      </BackofficeLayout>
    );
  }

  // Ensure properties and documents arrays always exist to prevent "length of undefined" errors
  const properties = association.properties || [];
  const documents = association.documents || [];

  return (
    <BackofficeLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center">
            <Button variant="outline" size="icon" onClick={() => navigate("/backoffice/associations")} className="mr-4">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <div className="flex items-center space-x-2">
                <h1 className="text-3xl font-bold tracking-tight">{association.name}</h1>
                <Badge>{association.association_type}</Badge>
              </div>
              <p className="text-gray-500">
                ID: {association.id}
              </p>
            </div>
          </div>
          <div className="flex space-x-2">
            <Button variant="outline" onClick={() => navigate(`/backoffice/associations/${id}/edit`)}>
              <Pencil className="mr-2 h-4 w-4" />
              Edit
            </Button>
            <Button variant="destructive">Delete</Button>
          </div>
        </div>

        <Tabs defaultValue="details">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="details">Details</TabsTrigger>
            <TabsTrigger value="properties">Properties</TabsTrigger>
            <TabsTrigger value="documents">Documents</TabsTrigger>
          </TabsList>
          
          <TabsContent value="details" className="space-y-6 pt-4">
            <Card>
              <CardHeader>
                <CardTitle>Association Information</CardTitle>
                <CardDescription>
                  Contact details and basic information
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-1">
                    <p className="text-sm font-medium flex items-center text-muted-foreground">
                      <Building className="h-4 w-4 mr-2" />
                      Type
                    </p>
                    <p>{association.type}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium flex items-center text-muted-foreground">
                      <MapPin className="h-4 w-4 mr-2" />
                      Address
                    </p>
                    <p>{association.address}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium flex items-center text-muted-foreground">
                      <Mail className="h-4 w-4 mr-2" />
                      Email
                    </p>
                    <p>{association.email}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium flex items-center text-muted-foreground">
                      <Phone className="h-4 w-4 mr-2" />
                      Phone
                    </p>
                    <p>{association.phone}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Financial Information</CardTitle>
                <CardDescription>
                  Tax ID and classification
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-muted-foreground">Tax ID (EIN)</p>
                    <p>{association.taxId}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-muted-foreground">Tax Classification</p>
                    <p>{association.taxClassification}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="properties" className="space-y-6 pt-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <div>
                  <CardTitle>Properties</CardTitle>
                  <CardDescription>
                    Total of {properties.length} properties
                  </CardDescription>
                </div>
                <Button>Add Property</Button>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="grid grid-cols-1 gap-4">
                  {properties.map((property) => (
                    <div 
                      key={property.id} 
                      className="p-4 border rounded-lg flex flex-col sm:flex-row sm:items-center justify-between"
                    >
                      <div className="flex items-start space-x-3">
                        <Home className="h-5 w-5 text-muted-foreground mt-0.5" />
                        <div>
                          <p className="font-medium">{property.address}</p>
                          <p className="text-sm text-muted-foreground">
                            {property.city}, {property.state} {property.zip}
                          </p>
                        </div>
                      </div>
                      <div className="flex space-x-2 mt-4 sm:mt-0">
                        <Button variant="outline" size="sm">Edit</Button>
                        <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-600">Delete</Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
              <CardFooter className="flex justify-between border-t pt-5">
                <p className="text-sm text-muted-foreground">
                  Showing {properties.length} properties
                </p>
                <Link to="/backoffice/properties" className="text-sm text-brand-blue hover:underline">
                  View All Properties
                </Link>
              </CardFooter>
            </Card>
          </TabsContent>
          
          <TabsContent value="documents" className="space-y-6 pt-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <div>
                  <CardTitle>Documents</CardTitle>
                  <CardDescription>
                    Total of {documents.length} documents
                  </CardDescription>
                </div>
                <Button>Add Document</Button>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="grid grid-cols-1 gap-4">
                  {documents.map((document) => (
                    <div 
                      key={document.id} 
                      className="p-4 border rounded-lg flex flex-col sm:flex-row sm:items-center justify-between"
                    >
                      <div className="flex items-start space-x-3">
                        <FileText className="h-5 w-5 text-muted-foreground mt-0.5" />
                        <div>
                          <p className="font-medium">{document.name}</p>
                          <div className="flex items-center mt-1">
                            <Badge variant="outline" className="text-xs">{document.type}</Badge>
                            <span className="text-sm text-muted-foreground ml-3">${document.price.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex space-x-2 mt-4 sm:mt-0">
                        <Button variant="outline" size="sm">Edit</Button>
                        <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-600">Delete</Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
              <CardFooter className="flex justify-between border-t pt-5">
                <p className="text-sm text-muted-foreground">
                  Showing {documents.length} documents
                </p>
                <Link to="/backoffice/documents" className="text-sm text-brand-blue hover:underline">
                  View All Documents
                </Link>
              </CardFooter>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </BackofficeLayout>
  );
};

export default AssociationDetail;
